import { WritableSignal, signal } from "@angular/core";
import {
  ActionSheetButton,
  AlertButton,
  AlertInput,
  ToastButton,
} from "@ionic/angular/standalone";
import { Title } from "@angular/platform-browser";
import { LogService } from "../../services/log.service";
import { ScriptService } from "../../services/script.service";
import { Logger } from "../../types/Log/Logger";

export class BasePage {
  public isLoading: WritableSignal<boolean> = signal<boolean>(false);

  public loadingMessage: WritableSignal<string> = signal<string>("Loading...");

  public alertIsOpen = signal<boolean>(false);

  public alertMessage: WritableSignal<string> = signal<string>("");

  public alertHeader: WritableSignal<string> = signal<string>("");

  public alertSubHeader: WritableSignal<string> = signal<string>("");

  public alertButtons: WritableSignal<AlertButton[]> = signal([]);

  public alertInputs: WritableSignal<AlertInput[]> = signal([]);

  public toastIsOpen = signal<boolean>(false);

  public toastMessage: WritableSignal<string> = signal<string>("");

  public toastHeader: WritableSignal<string> = signal<string>("");

  public toastButtons: WritableSignal<ToastButton[]> = signal([]);

  public toastColor: WritableSignal<string> = signal<string>("danger");

  public toastDuration: WritableSignal<number> = signal<number>(20000);

  public actionSheetIsOpen = signal<boolean>(false);

  public actionSheetButtons = signal<ActionSheetButton[]>([]);

  public actionSheetHeader = signal<string>("");

  public actionSheetSubHeader = signal<string>("");

  public viewWillEnter?(): Promise<void> | void;

  public scriptsDidRun?(): Promise<void> | void;

  public logger: Logger;

  constructor(
    protected logService: LogService,
    protected scriptService: ScriptService,
    protected serviceName: string,
    protected titleService: Title,
    protected pageName?: string,
  ) {
    this.logger = this.logService.registerLogger({
      service: this.serviceName,
    });
  }

  public updateScripts() {
    return this.scriptService.updateScripts(this.serviceName, this);
  }

  public async ionViewWillEnter() {
    if (this.pageName) {
      this.titleService.setTitle(`${this.pageName} | HSCloud Mobile`);
    }
    if (typeof this.viewWillEnter === "function") {
      await this.viewWillEnter();
    }
    await this.updateScripts();
    if (typeof this.scriptsDidRun === "function") {
      await this.scriptsDidRun();
    }
  }

  public openToast(
    header: string,
    message: string,
    color: string = "danger",
    duration: number = 20000,
    buttons: ToastButton[] = [
      {
        text: "Dismiss",
        role: "cancel",
      },
    ],
  ) {
    this.toastHeader.set(header);
    this.toastColor.set(color);
    this.toastMessage.set(message);
    this.toastDuration.set(duration);
    this.toastButtons.set(buttons);
    this.toastIsOpen.set(true);
  }

  public openAlert(
    header: string,
    subheader: string,
    message: string,
    buttons: AlertButton[],
    inputs?: AlertInput[],
  ) {
    this.alertHeader.set(header);
    this.alertSubHeader.set(subheader);
    this.alertMessage.set(message);
    this.alertButtons.set(buttons);
    this.alertInputs.set(inputs || []);
    this.alertIsOpen.set(true);
  }

  public openLoading(message: string) {
    this.loadingMessage.set(message);
    this.isLoading.set(true);
  }

  public openActionSheet(
    buttons: ActionSheetButton[],
    header: string,
    subHeader?: string,
  ) {
    this.actionSheetButtons.set(buttons);
    this.actionSheetHeader.set(header);
    this.actionSheetSubHeader.set(subHeader || "");
    this.actionSheetIsOpen.set(true);
  }

  public toggleActionSheet() {
    this.actionSheetIsOpen.set(!this.actionSheetIsOpen());
  }

  public hideToast(): void {
    this.toastIsOpen.set(false);
  }

  public closeAlert(): void {
    this.alertIsOpen.set(false);
  }
}
